import { useState, useEffect, Suspense, lazy } from 'react';
import { useAuth } from './hooks/useAuth';
import { cn } from './lib/utils';
import { LoadingSpinner } from './components/LoadingSpinner';
import { ErrorBoundary } from './components/ErrorBoundary';
import { usePrefetch } from './utils/prefetch';

// Lazy loading des composants
const Navbar = lazy(() => import('./components/Navbar'));
const Footer = lazy(() => import('./components/Footer'));
const AuthModal = lazy(() => import('./components/AuthModal'));
const AppRoutes = lazy(() => import('./routes/AppRoutes'));

// Composant de chargement pour les composants principaux
const ComponentLoadingSpinner = () => (
    <div className="flex justify-center items-center py-4">
        <LoadingSpinner size="md" />
    </div>
);

const AppContent: React.FC = () => {
    const { isAuthModalOpen, closeAuthModal } = useAuth();
    const [isBannerVisible, setIsBannerVisible] = useState(true);

    // Activer le prefetching intelligent
    usePrefetch();

    useEffect(() => {
        // Vérifier l'état initial de la bannière
        const bannerClosed = localStorage.getItem('appBannerClosed');
        setIsBannerVisible(!bannerClosed);

        // Écouter les changements de visibilité de la bannière
        const handleBannerChange = () => {
            setIsBannerVisible(false);
        };

        window.addEventListener('bannerVisibilityChange', handleBannerChange);

        return () => {
            window.removeEventListener('bannerVisibilityChange', handleBannerChange);
        };
    }, []);

    return (
        <ErrorBoundary>
            <div id="top" className={cn(
                "min-h-screen hero-gradient relative overflow-hidden",
                isBannerVisible ? "pt-[140px]" : "pt-0"
            )}>
                <Suspense fallback={<ComponentLoadingSpinner />}>
                    <Navbar />
                </Suspense>
                
                <main className="pt-32">
                    <ErrorBoundary>
                        <Suspense fallback={<ComponentLoadingSpinner />}>
                            <AppRoutes />
                        </Suspense>
                    </ErrorBoundary>
                </main>

                <Suspense fallback={<ComponentLoadingSpinner />}>
                    <Footer />
                </Suspense>

                <Suspense fallback={null}>
                    <AuthModal isOpen={isAuthModalOpen} onClose={closeAuthModal} />
                </Suspense>
            </div>
        </ErrorBoundary>
    );
};

export default AppContent;